import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Store, Star, PanelTop, Home, MapPin, FileText, Mail, Package,
  ArrowLeft, Check, Loader2, ArrowRight, ShieldCheck, Target, Users, X, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const ICONS = { store: Store, star: Star, banner: PanelTop, homepage: Home, state: MapPin, feature: FileText, newsletter: Mail, bundle: Package };
const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

function priceLabel(p, annual) {
  if (p.billing_type === "one_time") return { big: dollars(p.one_time_cents), unit: p.key === "newsletter_sponsor" ? "per send" : "one-time" };
  if (annual) return { big: dollars(p.annual_cents), unit: "/yr billed annually" };
  return { big: dollars(p.monthly_cents), unit: "/mo" };
}

const FAQS = [
  ["Can I cancel my advertising?", "Yes. Recurring advertising can be cancelled anytime from your advertiser dashboard — it stays active until the end of your current billing period."],
  ["How quickly will my ad go live?", "As soon as you complete your advertising setup, our team reviews it. Most placements are approved and live within 1–2 business days."],
  ["Can I change my advertisement?", "Absolutely. Update your logo, creative, links and copy anytime from your dashboard — changes are re-reviewed before going live."],
  ["What businesses can advertise?", "Outfitters, guides, taxidermists, meat processors, lodges, ranches, archery shops, outdoor stores, dog trainers, local services and smaller hunting brands."],
  ["How does state sponsorship work?", "Each state page has one exclusive sponsor. Pick an available state at checkout — your logo and \u201cpresented by\u201d branding appears on that state's hunting page."],
  ["Are advertisements reviewed?", "Yes. Every advertisement is reviewed by HomeRange before it appears publicly to keep the platform trustworthy for hunters."],
  ["Can I advertise in multiple states?", "Yes — purchase a State Page Sponsor for each state you want, subject to availability."],
  ["Do you offer annual pricing?", "Yes. Switch the billing toggle to Annual and save 20% on all recurring advertising packages."],
];

function AdCard({ p, annual, onSelect, busy }) {
  const Icon = ICONS[p.icon] || Store;
  const price = priceLabel(p, annual && p.billing_type === "recurring");
  const featured = p.featured;
  return (
    <motion.button
      whileHover={{ y: -4 }}
      onClick={() => onSelect(p)}
      disabled={busy}
      className={`text-left p-6 border flex flex-col h-full transition-colors disabled:opacity-60 ${featured ? "border-blaze bg-forest text-bone" : "border-saddle/20 bg-white hover:border-forest"}`}
      data-testid={`ad-card-${p.key}`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-12 h-12 grid place-items-center ${featured ? "bg-blaze text-white" : "bg-bone text-forest"}`}>
          <Icon size={22} />
        </div>
        {p.badge && <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 ${featured ? "bg-blaze text-white" : "bg-saddle/15 text-saddle"}`} data-testid={`ad-badge-${p.key}`}>{p.badge}</span>}
      </div>
      <h3 className={`font-serif text-xl mt-4 ${featured ? "text-bone" : "text-forest"}`}>{p.name}</h3>
      <p className={`text-sm mt-1 ${featured ? "text-bone/70" : "text-charcoal/60"}`}>{p.description}</p>
      <div className="mt-4">
        <span className="font-serif text-3xl tabular-nums">{price.big}</span>
        <span className={`text-xs ml-1 ${featured ? "text-bone/60" : "text-charcoal/50"}`}>{price.unit}</span>
      </div>
      <ul className="mt-4 space-y-1.5 flex-1">
        {p.features.map((f, i) => (
          <li key={i} className={`text-xs flex items-start gap-1.5 ${featured ? "text-bone/80" : "text-charcoal/70"}`}>
            <Check size={13} className={`mt-0.5 shrink-0 ${featured ? "text-blaze" : "text-forest"}`} /> {f}
          </li>
        ))}
      </ul>
      <span className={`mt-5 inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold w-full ${featured ? "bg-blaze text-white" : "bg-forest text-bone"}`} data-testid={`ad-cta-${p.key}`}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <>{p.cta} <ArrowRight size={15} /></>}
      </span>
    </motion.button>
  );
}

function StateModal({ product, annual, onClose, onConfirm, busy }) {
  const [states, setStates] = useState(null);
  const [selected, setSelected] = useState("");
  useEffect(() => { api.adStateAvailability().then((d) => setStates(d.states)).catch(() => setStates([])); }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-forest/60 backdrop-blur-sm grid place-items-center px-4" data-testid="state-modal" onClick={onClose}>
      <div className="bg-paper max-w-lg w-full p-7 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <span className="hr-overline text-saddle">State Page Sponsor</span>
            <h3 className="font-serif text-2xl text-forest mt-1">Choose a state to sponsor</h3>
            <p className="text-charcoal/60 text-sm mt-1">One exclusive sponsor per state. Unavailable states are already claimed.</p>
          </div>
          <button onClick={onClose} className="text-charcoal/50 hover:text-forest" aria-label="Close" data-testid="state-modal-close"><X size={20} /></button>
        </div>
        {!states ? (
          <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-5" data-testid="state-grid">
            {states.map((s) => (
              <button
                key={s.state}
                disabled={!s.available}
                onClick={() => setSelected(s.state)}
                className={`text-left px-3 py-2 text-sm border transition-colors ${!s.available ? "opacity-40 cursor-not-allowed border-saddle/15 line-through" : selected === s.state ? "border-forest bg-forest text-bone" : "border-saddle/25 hover:border-forest"}`}
                data-testid={`state-opt-${s.state.replace(/\s/g, "-")}`}
              >
                {s.state}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={() => onConfirm(selected)}
          disabled={!selected || busy}
          className="mt-6 w-full bg-blaze text-white py-3 font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="state-confirm"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <>Sponsor {selected || "a state"} — {dollars(annual ? product.annual_cents : product.monthly_cents)}{annual ? "/yr" : "/mo"}</>}
        </button>
      </div>
    </div>
  );
}

function PartnershipModal({ onClose }) {
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", website: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.company_name || !form.contact_name || !form.email) return toast.error("Company, name and email are required");
    setBusy(true);
    try { await api.partnershipInquiry(form); toast.success("Thanks — the HomeRange partnerships team will be in touch."); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send inquiry"); }
    finally { setBusy(false); }
  };
  const F = (k, ph, type = "text") => (
    <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} type={type}
      className="w-full border border-saddle/25 px-3 py-2.5 text-sm outline-none focus:border-forest" data-testid={`partner-${k}`} />
  );
  return (
    <div className="fixed inset-0 z-[60] bg-forest/60 backdrop-blur-sm grid place-items-center px-4" data-testid="partnership-modal" onClick={onClose}>
      <div className="bg-paper max-w-lg w-full p-7 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <span className="hr-overline text-saddle">HomeRange Brand Partnerships</span>
            <h3 className="font-serif text-2xl text-forest mt-1">Looking for a larger partnership?</h3>
            <p className="text-charcoal/60 text-sm mt-1">Custom national partnerships, product integrations, category sponsorships and exclusive placements for major outdoor brands.</p>
          </div>
          <button onClick={onClose} className="text-charcoal/50 hover:text-forest" aria-label="Close" data-testid="partnership-close"><X size={20} /></button>
        </div>
        <div className="space-y-3 mt-5">
          {F("company_name", "Company name")}
          {F("contact_name", "Contact name")}
          {F("email", "Email", "email")}
          {F("phone", "Phone (optional)")}
          {F("website", "Website (optional)")}
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Tell us what you're looking for" className="w-full border border-saddle/25 px-3 py-2.5 text-sm outline-none focus:border-forest" data-testid="partner-message" />
          <button onClick={submit} disabled={busy} className="w-full bg-forest text-bone py-3 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="partner-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Contact HomeRange Partnerships"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-saddle/20" data-testid="faq-item">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left" data-testid="faq-toggle">
        <span className="font-semibold text-forest">{q}</span>
        <ChevronDown size={18} className={`text-saddle transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-charcoal/70 text-sm pb-4 -mt-1" data-testid="faq-answer">{a}</p>}
    </div>
  );
}

export default function Advertise() {
  const navigate = useNavigate();
  const { isAuthed, ready } = useAuth();
  const { openAuth } = useUI();
  const [products, setProducts] = useState([]);
  const [annual, setAnnual] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [stateProduct, setStateProduct] = useState(null);
  const [showPartner, setShowPartner] = useState(false);

  useEffect(() => { api.adProducts().then((d) => setProducts(d.products)).catch(() => {}); }, []);

  const checkout = async (product, state = null) => {
    if (!ready) return;
    if (!isAuthed) { toast.message("Sign in to purchase advertising"); openAuth("register"); return; }
    setBusyKey(product.key);
    try {
      const res = await api.adCheckout({
        product_key: product.key,
        billing_cycle: annual ? "annual" : "monthly",
        state,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusyKey(null);
    }
  };

  const onSelect = (p) => {
    if (p.requires_state) { setStateProduct(p); return; }
    checkout(p);
  };

  const bundle = products.find((p) => p.featured);
  const grid = products.filter((p) => !p.featured);

  return (
    <div className="min-h-screen bg-paper" data-testid="advertise-page">
      <SiteHeader />

      {/* Hero */}
      <section className="bg-forest text-bone grain relative overflow-hidden px-5 sm:px-10 py-20 sm:py-28" data-testid="advertise-hero">
        <div className="relative z-10 max-w-[1100px] mx-auto text-center">
          <div className="flex justify-center mb-8">
            <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-bone/60 hover:text-bone" data-testid="advertise-back"><ArrowLeft size={15} /> Back to HomeRange</button>
          </div>
          <span className="hr-overline text-blaze block">Advertise With HomeRange</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none tracking-tight">Put Your Business in Front of the Hunting Community</h1>
          <p className="text-bone/70 mt-5 max-w-2xl mx-auto">HomeRange connects hunters with the brands, businesses, services, destinations and resources that make hunting possible. Choose an advertising option below to put your business directly in front of the HomeRange community.</p>
          <a href="#packages" className="mt-8 inline-flex items-center gap-2 bg-blaze text-white px-7 py-3.5 font-semibold hover:bg-blaze-dark transition-colors" data-testid="advertise-hero-cta">See advertising options <ArrowRight size={16} /></a>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="px-5 sm:px-10 py-16 sm:py-24 scroll-mt-20" data-testid="advertise-packages">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10">
            <div>
              <span className="hr-overline text-saddle">Choose your advertising package</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-2 leading-none">Self-service advertising, live in days.</h2>
            </div>
            {/* Billing toggle */}
            <div className="inline-flex items-center bg-white border border-saddle/25 p-1 self-start" data-testid="billing-toggle">
              <button onClick={() => setAnnual(false)} className={`px-4 py-2 text-sm font-semibold transition-colors ${!annual ? "bg-forest text-bone" : "text-charcoal/60"}`} data-testid="toggle-monthly">Monthly</button>
              <button onClick={() => setAnnual(true)} className={`px-4 py-2 text-sm font-semibold transition-colors ${annual ? "bg-forest text-bone" : "text-charcoal/60"}`} data-testid="toggle-annual">Annual · Save 20%</button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ad-cards-grid">
            {grid.map((p) => <AdCard key={p.key} p={p} annual={annual} onSelect={onSelect} busy={busyKey === p.key} />)}
          </div>
        </div>
      </section>

      {/* Premium bundle */}
      {bundle && (
        <section className="px-5 sm:px-10 pb-16 sm:pb-24" data-testid="advertise-bundle">
          <div className="max-w-[1500px] mx-auto bg-forest text-bone grain relative overflow-hidden">
            <div className="relative z-10 grid lg:grid-cols-2 gap-8 p-8 sm:p-12 items-center">
              <div>
                <span className="inline-block bg-blaze text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1">{bundle.badge}</span>
                <h2 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">{bundle.name}</h2>
                <p className="text-bone/70 mt-3">{bundle.description}</p>
                <div className="mt-5">
                  <span className="font-serif text-5xl tabular-nums">{dollars(annual ? bundle.annual_cents : bundle.monthly_cents)}</span>
                  <span className="text-bone/60 ml-1">{annual ? "/yr billed annually" : "/mo"}</span>
                </div>
                <button onClick={() => onSelect(bundle)} disabled={busyKey === bundle.key} className="mt-6 inline-flex items-center gap-2 bg-blaze text-white px-7 py-3.5 font-semibold hover:bg-blaze-dark transition-colors disabled:opacity-60" data-testid="bundle-cta">
                  {busyKey === bundle.key ? <Loader2 size={16} className="animate-spin" /> : <>{bundle.cta} <ArrowRight size={16} /></>}
                </button>
              </div>
              <ul className="grid gap-3">
                {bundle.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 bg-forest-2/60 px-4 py-3"><Check size={16} className="text-blaze shrink-0" /> <span className="text-bone/90">{f}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Why advertise */}
      <section className="bg-bone px-5 sm:px-10 py-16 sm:py-24" data-testid="advertise-why">
        <div className="max-w-[1300px] mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <span className="hr-overline text-saddle">Why advertise with HomeRange?</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-2">Targeted access to hunters and the outdoor industry.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              [Target, "Reach real hunters", "Your business appears where hunters already plan trips, buy gear and read regulations — not a generic audience."],
              [Users, "Built for the outdoors", "From outfitters to archery shops, HomeRange is purpose-built for the hunting and outdoor community."],
              [ShieldCheck, "Trustworthy placements", "Every ad is reviewed and clearly identified, keeping the platform credible for the hunters you want to reach."],
            ].map(([Icon, t, d], i) => (
              <div key={i} className="bg-white border border-saddle/20 p-6" data-testid="why-card">
                <div className="w-11 h-11 grid place-items-center bg-forest text-bone"><Icon size={20} /></div>
                <h3 className="font-serif text-xl text-forest mt-4">{t}</h3>
                <p className="text-charcoal/60 text-sm mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand partnership */}
      <section className="px-5 sm:px-10 py-16 sm:py-24" data-testid="advertise-partnership">
        <div className="max-w-[1100px] mx-auto border border-saddle/25 bg-white p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <span className="hr-overline text-saddle">Major brand?</span>
            <h2 className="font-serif text-3xl text-forest mt-2">Looking for something bigger?</h2>
            <p className="text-charcoal/60 mt-3 max-w-xl">HomeRange offers custom national partnerships, product integrations, category sponsorships, campaigns, exclusive placements and platform integrations for major outdoor brands.</p>
          </div>
          <button onClick={() => setShowPartner(true)} className="shrink-0 bg-forest text-bone px-7 py-3.5 font-semibold hover:bg-forest-2 transition-colors" data-testid="partnership-cta">Become a HomeRange Brand Partner</button>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-bone px-5 sm:px-10 py-16 sm:py-24" data-testid="advertise-faq">
        <div className="max-w-[820px] mx-auto">
          <span className="hr-overline text-saddle">FAQ</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-2 mb-6">Advertising questions, answered.</h2>
          <div>{FAQS.map(([q, a]) => <FAQItem key={q} q={q} a={a} />)}</div>
        </div>
      </section>

      <Footer />

      {stateProduct && (
        <StateModal
          product={stateProduct}
          annual={annual}
          busy={busyKey === stateProduct.key}
          onClose={() => setStateProduct(null)}
          onConfirm={(state) => { if (state) checkout(stateProduct, state); }}
        />
      )}
      {showPartner && <PartnershipModal onClose={() => setShowPartner(false)} />}
    </div>
  );
}
